import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  /** @type {number | null} */
  #idleTimer = null;
  /** Delay before the play/pause hint fades out when the mouse stops moving. */
  #idleDelay = 1000;
  /** @type {boolean} */
  #videoListenersAttached = false;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
    this.addEventListener('mousemove', this.#handleMouseMove, { signal });
    if (this.getAttribute("autoplay") != null) {
      // Attach listeners now since the <video> is eagerly rendered for autoplay
      // (no loadContent call). Don't optimistically set isPlaying — the 'play'
      // event will flip it when playback actually starts. On iOS Low Power Mode
      // autoplay is blocked, so the play event never fires and the play icon
      // correctly stays visible until the user taps.
      this.#attachVideoListeners();
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#clearIdleTimer();
  }

  #handleMouseMove = () => {
    this.classList.remove('is-idle');
    if (this.isPlaying) {
      this.#restartIdleTimer();
    }
  };

  #restartIdleTimer() {
    if (this.#idleTimer != null) {
      window.clearTimeout(this.#idleTimer);
    }
    this.#idleTimer = window.setTimeout(() => {
      this.classList.add('is-idle');
      this.#idleTimer = null;
    }, this.#idleDelay);
  }

  #clearIdleTimer() {
    if (this.#idleTimer != null) {
      window.clearTimeout(this.#idleTimer);
      this.#idleTimer = null;
    }
    this.classList.remove('is-idle');
  }

  /**
   * Attach play/pause/ended listeners to the inner <video> element.
   * Idempotent — safe to call from both connectedCallback (for eagerly
   * rendered autoplay videos) and loadContent (for click-to-play).
   * Listeners use the abort controller signal for clean teardown.
   */
  #attachVideoListeners() {
    if (this.#videoListenersAttached) return;
    const videoEl = this.querySelector('video');
    if (!videoEl) return;
    this.#videoListenersAttached = true;
    const signal = this.#abortController.signal;
    videoEl.addEventListener('play', () => {
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
      this.#restartIdleTimer();
      // From the real 'play' event rather than playMedia()'s return value: lazy videos
      // often reject the first play() while their sources load, then start on their own.
      this.#retirePosterButton();
    }, { signal });
    videoEl.addEventListener('pause', () => {
      this.isPlaying = false;
      this.updatePlayPauseHint(this.isPlaying);
      this.#clearIdleTimer();
    }, { signal });
    videoEl.addEventListener('ended', () => {
      this.isPlaying = false;
      this.updatePlayPauseHint(this.isPlaying);
      this.#clearIdleTimer();
    }, { signal });
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
      const label = isPlaying ? toggleMediaButton.dataset.labelPause : toggleMediaButton.dataset.labelPlay;
      if (label) toggleMediaButton.setAttribute('aria-label', label);
    }
  }

  /**
   * Once playback starts, the poster's Play button is redundant: the toggle button
   * takes over play/pause. Left as it was, it stayed in the tab order still announcing
   * "Play" (and pressing it again didn't pause), with the real control after it.
   * Hands focus to the toggle when the poster button had it -- keyboard and screen
   * reader users -- then takes the poster button out of the tab order and the
   * accessibility tree. Not `inert`: that would also swallow mouse clicks on it.
   * Only runs when there is a toggle; product-media posters have none and are left alone.
   */
  #retirePosterButton() {
    const { deferredMediaPlayButton: posterButton, toggleMediaButton } = this.refs;
    if (!(posterButton instanceof HTMLElement) || !(toggleMediaButton instanceof HTMLElement)) return;
    if (posterButton.getAttribute('aria-hidden') === 'true') return;

    if (document.activeElement === posterButton) toggleMediaButton.focus();
    posterButton.setAttribute('tabindex', '-1');
    posterButton.setAttribute('aria-hidden', 'true');
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = async () => {
    this.loadContent(true);
    const didPlay = await this.playMedia();

    if (didPlay !== false) {
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    }
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    this.#attachVideoListeners();

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    // showDeferredMedia/playMedia handles playback after sources are ready.
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  async playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
      this.#restartIdleTimer();
      // Embedded players fire no 'play' event on our side, so retire the poster here.
      this.#retirePosterButton();
      return true;
    } else {
      const video = this.querySelector('video');
      if (!video) return false;

      const readyPromise = window.HiNoteLazyVideo?.load?.(video) ?? Promise.resolve(true);
      const playPromise = video.play();
      const didPlay = playPromise instanceof Promise ? await playPromise.then(() => true).catch(() => false) : true;

      await readyPromise.catch(() => false);

      this.isPlaying = didPlay && !video.paused;
      this.updatePlayPauseHint(this.isPlaying);
      if (this.isPlaying) {
        this.#restartIdleTimer();
      }
      return this.isPlaying;
    }
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;
    this.#clearIdleTimer();

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute("autoplay") != null) {
      this.updatePlayPauseHint(this.isPlaying);
    }else {
      if (this.getAttribute('data-media-loaded')) {
        this.updatePlayPauseHint(this.isPlaying);
      }
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
